import type { ServiceRow } from '@busy/api';

// Shared naming helpers for the Cut Timer service/add-on catalog. Lives
// here (not in @busy/api) because it's pure UI logic — the source of
// truth for "is this name available?" is the partial unique index on
// the services table (migration 0055) plus the pre-flight checks in
// createService/updateService. These helpers exist to compute a SENSIBLE
// default name on the client BEFORE the user is asked to confirm, so
// duplicating a row doesn't dead-end at a validation error.
//
// Numeric suffix policy: " 2", " 3", … (no parens, iOS-Contacts style).
// We use a trailing " N" the user can read at a glance — matches Apple's
// own "Marcus 2" duplicate-contact convention from the Contacts app.

/**
 * Given a list of existing services (or add-ons) and a base name, return
 * the smallest name in the sequence
 *   base, base 2, base 3, …
 * that is not already taken by an ACTIVE (non-archived) row of the same
 * is_addon kind. Case- and whitespace-insensitive comparison, matching
 * the DB unique index.
 *
 * If the trailing token of `baseName` is already a number (e.g. the
 * source row is itself "Beard Trim 2"), the function strips it before
 * incrementing — so duplicating "Beard Trim 2" yields "Beard Trim 3",
 * not "Beard Trim 2 2".
 */
export function uniqueServiceName(
  existing: ServiceRow[],
  isAddon: boolean,
  baseName: string,
): string {
  const trimmed = baseName.trim();
  if (!trimmed) return trimmed;

  // Strip a trailing " N" so dup-of-a-dup doesn't stack ("Beard Trim 2 2").
  const root = trimmed.replace(/\s+\d+$/, '');
  const rootLower = root.toLowerCase();

  // Normalize the existing-name set the same way the partial unique index
  // does (lower(trim(name))) + filter to the same is_addon namespace +
  // non-archived rows — those are the ones that would actually collide.
  const taken = new Set<string>();
  for (const s of existing) {
    if (s.is_addon !== isAddon) continue;
    if (s.is_archived) continue;
    taken.add(s.name.trim().toLowerCase());
  }

  // If the bare root is free, use it.
  if (!taken.has(rootLower)) return root;

  // Otherwise scan upward. The cap is a defensive escape hatch — a
  // barber will never realistically have 999 copies of one service.
  for (let n = 2; n < 1000; n++) {
    const candidate = `${root} ${n}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
  return `${root} ${Date.now()}`; // unreachable in practice
}
